import _ from 'lodash'
import { baseApi, Tag } from './base'
import { WaitingRoom, WaitingRoomActiveConsultation, WaitingRoomConsultationPreview, WaitingRoomFairStatus } from '../models/waiting-room.model'
import { SearchRequestOptions, UpdateRequest } from '../types/request'
import { SearchResponse } from '../types/response'
import { Staff } from '../models/staff.model'
import { cacher } from '@/utils/rtkQueryCacheUtils'
import { CreateSpecialityOptionDto } from '../models/speciality-option.model'
import { getSocket } from './socket'
import { SocketEvent } from '../types/socket-event'
import { Consultation } from '../models/consultation.model'
import { mergeWaitingRoomUpdate } from './waiting-room-cache.util'

export type CreateWaitingRoomParams = {
  avgWaitingTime: number
  allowPatientSelection?: boolean
  description?: string
  doctors: string[]
  establishments: string[]
  isPrioritized?: boolean
  name: string
  network?: string
  openingHours: Array<{
    day: number
    periods: Array<{ open: string; close: string }>
  }>
  price?: number
  paymentReceiverId?: string
  specialities: CreateSpecialityOptionDto[]
  fairDistribution?: {
    enabled: boolean
    maxConcurrent: number
    cooldownSeconds: number
    strategy?: 'cap' | 'offer'
    offerTtlSeconds?: number
    prominentOffer?: boolean
  }
  readinessConfig?: { enabled: boolean; prioritizeReady: boolean; checklistTemplateId?: string }
  coPresenceConfig?: { enabled: boolean; allowAssign?: boolean }
}

export type ListWaitingRoomsParams = {
  _id?: string | { $in: string[] }
  doctors?: string[]
  isPrioritized?: boolean
  name?: string
  network?: string
  specialities?: string[]
}

type AcceptWaitingRoomConsultationParams = { consultationId: string; doctorId: string; waitingRoomId: string }

// One entry per ONLINE doctor of the room (offline doctors are simply absent) — same shape as the
// WAITING_ROOM_DOCTORS_STATUS socket snapshot, which only tracked doctors receive.
export type WaitingRoomDoctorStatus = {
  doctorId: string
  status: 'available' | 'in_consultation'
  consultationId?: string
}

export const waitingRoomApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createWaitingRoom: builder.mutation<WaitingRoom, CreateWaitingRoomParams>({
      query: (body) => ({ url: '/waiting-rooms', method: 'POST', body }),
      invalidatesTags: cacher.invalidatesList(Tag.WAITING_ROOM),
    }),
    getWaitingRoom: builder.query<WaitingRoom, string>({
      query: (waitingRoomId) => ({ url: `/waiting-rooms/${waitingRoomId}` }),
      async onCacheEntryAdded(waitingRoomId, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        const socket = await getSocket()
        const waitingRoomUpdatedListener = (updatedWaitingRoom: WaitingRoom) => {
          // Guard against broadcasts that carry UNPOPULATED consultations (raw ObjectIds), which
          // would otherwise blank the queue. See mergeWaitingRoomUpdate.
          updateCachedData((cachedWaitingRoom) => {
            if (cachedWaitingRoom._id !== updatedWaitingRoom._id) return
            mergeWaitingRoomUpdate(cachedWaitingRoom, updatedWaitingRoom)
          })
        }
        // socket.io room membership is per-connection and is NOT preserved across a reconnect (new
        // socket id, no connectionStateRecovery; the server only auto-rejoins the general/userId
        // rooms on connect). After any disconnect — macOS sleep/wake, Wi-Fi change, network blip —
        // we must re-emit ENTER, otherwise waiting_room_updated silently stops arriving and the
        // patient count freezes (no sound / no notification) until a manual refresh.
        const reEnterWaitingRoom = () => socket.emit(SocketEvent.ENTER_WAITING_ROOM, waitingRoomId)
        try {
          await cacheDataLoaded
          socket.on(SocketEvent.WAITING_ROOM_UPDATED, waitingRoomUpdatedListener)
          socket.emit(SocketEvent.ENTER_WAITING_ROOM, waitingRoomId)
          socket.on('connect', reEnterWaitingRoom)
        } catch {}
        await cacheEntryRemoved
        socket.off(SocketEvent.WAITING_ROOM_UPDATED, waitingRoomUpdatedListener)
        socket.off('connect', reEnterWaitingRoom)
        socket.emit(SocketEvent.LEAVE_WAITING_ROOM, waitingRoomId)
      },
      providesTags: (_result, _error, waitingRoomId) => [{ type: Tag.WAITING_ROOM, id: waitingRoomId }],
    }),
    getManageableWaitingRooms: builder.query<WaitingRoom[], void>({
      query: () => ({ url: '/waiting-rooms/manageable' }),
      providesTags: cacher.providesList(Tag.WAITING_ROOM),
    }),
    listWaitingRooms: builder.query<SearchResponse<WaitingRoom>, { query: ListWaitingRoomsParams; options?: SearchRequestOptions }>({
      query: ({ query, options }) => ({
        url: `/waiting-rooms/query`,
        method: 'POST',
        body: { query, options: _.merge({}, { limit: 5, order: 'ASC', skip: 0 }, options) },
      }),
      providesTags: cacher.providesPaginatedList(Tag.WAITING_ROOM),
    }),
    updateWaitingRoom: builder.mutation<WaitingRoom, UpdateRequest<Partial<CreateWaitingRoomParams>>>({
      query: ({ id: waitingRoomId, updates }) => ({ url: `/waiting-rooms/${waitingRoomId}`, method: 'PUT', body: updates }),
      async onQueryStarted({ id: waitingRoomId }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedWaitingRoom } = await queryFulfilled
          dispatch(
            waitingRoomApi.util.updateQueryData('getWaitingRoom', waitingRoomId, (cachedWaitingRoom) => {
              Object.assign(cachedWaitingRoom, updatedWaitingRoom)
            })
          )
        } catch {}
      },
    }),
    deleteWaitingRoom: builder.mutation<string, string>({
      query: (waitingRoomId) => ({ url: `/waiting-rooms/${waitingRoomId}`, method: 'DELETE' }),
      invalidatesTags: cacher.invalidatesList(Tag.WAITING_ROOM),
    }),
    getWaitingRoomDoctors: builder.query<Staff[], string>({
      query: (waitingRoomId) => ({ url: `/waiting-rooms/${waitingRoomId}/doctors` }),
    }),
    getWaitingRoomDoctorsStatus: builder.query<WaitingRoomDoctorStatus[], string>({
      query: (waitingRoomId) => ({ url: `/waiting-rooms/${waitingRoomId}/doctors-status` }),
    }),
    getMyWaitingRoomFairStatus: builder.query<WaitingRoomFairStatus, string>({
      query: (waitingRoomId) => ({ url: `/waiting-rooms/${waitingRoomId}/my-fair-status` }),
      // The cap count and cooldown change when patients are accepted/ended (which broadcast
      // WAITING_ROOM_UPDATED), so refetch this doctor-scoped status whenever the room changes.
      async onCacheEntryAdded(waitingRoomId, { dispatch, cacheDataLoaded, cacheEntryRemoved }) {
        const socket = await getSocket()
        const invalidate = () => {
          dispatch(waitingRoomApi.util.invalidateTags([{ type: Tag.WAITING_ROOM, id: `${waitingRoomId}-fair-status` }]))
        }
        // 'offer' mode rotates the reservation over its own event (it does not change the room
        // payload), so the doctor's turn/countdown must also refetch on FAIR_TURN_OFFER_UPDATED.
        const onOfferUpdated = (payload: { waitingRoomId: string }) => {
          if (payload?.waitingRoomId === waitingRoomId) invalidate()
        }
        try {
          await cacheDataLoaded
          socket.on(SocketEvent.WAITING_ROOM_UPDATED, invalidate)
          socket.on(SocketEvent.FAIR_TURN_OFFER_UPDATED, onOfferUpdated)
        } catch {}
        await cacheEntryRemoved
        socket.off(SocketEvent.WAITING_ROOM_UPDATED, invalidate)
        socket.off(SocketEvent.FAIR_TURN_OFFER_UPDATED, onOfferUpdated)
      },
      providesTags: (_result, _error, waitingRoomId) => [{ type: Tag.WAITING_ROOM, id: `${waitingRoomId}-fair-status` }],
    }),
    getWaitingRoomActiveConsultations: builder.query<WaitingRoomActiveConsultation[], string>({
      query: (waitingRoomId) => ({ url: `/waiting-rooms/${waitingRoomId}/active-consultations` }),
      async onCacheEntryAdded(waitingRoomId, { dispatch, cacheDataLoaded, cacheEntryRemoved }) {
        const socket = await getSocket()
        const invalidate = (updatedWaitingRoom?: WaitingRoom) => {
          if (!updatedWaitingRoom || updatedWaitingRoom._id === waitingRoomId) {
            dispatch(waitingRoomApi.util.invalidateTags([{ type: Tag.WAITING_ROOM, id: `${waitingRoomId}-active-consultations` }]))
          }
        }
        try {
          await cacheDataLoaded
          socket.on(SocketEvent.WAITING_ROOM_UPDATED, invalidate)
        } catch {}
        await cacheEntryRemoved
        socket.off(SocketEvent.WAITING_ROOM_UPDATED, invalidate)
      },
      providesTags: (_result, _error, waitingRoomId) => [{ type: Tag.WAITING_ROOM, id: `${waitingRoomId}-active-consultations` }],
    }),
    // SEHA pre-acceptance preview: read-only case summary (nurse comment, vitals, documents,
    // implicated staff). keepUnusedDataFor 0 + refetch on arg change so the drawer is always fresh.
    getWaitingRoomConsultationPreview: builder.query<WaitingRoomConsultationPreview, { waitingRoomId: string; consultationId: string }>({
      query: ({ waitingRoomId, consultationId }) => ({ url: `/waiting-rooms/${waitingRoomId}/consultations/${consultationId}/preview` }),
      keepUnusedDataFor: 0,
    }),
    acceptWaitingRoomConsultation: builder.mutation<{ waitingRoom: WaitingRoom; consultation: Consultation }, AcceptWaitingRoomConsultationParams>({
      query: ({ waitingRoomId, ...body }) => ({
        url: `/waiting-rooms/${waitingRoomId}/accept-consultation`,
        method: 'POST',
        body,
      }),
    }),
    joinWaitingRoomConsultation: builder.mutation<{ consultation: Consultation }, { waitingRoomId: string; consultationId: string; assign: boolean }>({
      query: ({ waitingRoomId, consultationId, assign }) => ({
        url: `/waiting-rooms/${waitingRoomId}/consultations/${consultationId}/co-present`,
        method: 'POST',
        body: { assign },
      }),
      invalidatesTags: (_result, _error, { waitingRoomId }) => [
        { type: Tag.WAITING_ROOM, id: waitingRoomId },
        { type: Tag.WAITING_ROOM, id: `${waitingRoomId}-active-consultations` },
        { type: Tag.WAITING_ROOM, id: `${waitingRoomId}-fair-status` },
      ],
    }),
    // 'offer' mode: the offered doctor declines ("Not now") to rotate the patient to the next doctor.
    passWaitingRoomOffer: builder.mutation<{ success: boolean }, { waitingRoomId: string; consultationId: string }>({
      query: ({ waitingRoomId, consultationId }) => ({
        url: `/waiting-rooms/${waitingRoomId}/pass-offer`,
        method: 'POST',
        body: { consultationId },
      }),
    }),
    joinWaitingRoom: builder.mutation<WaitingRoom, string>({
      query: (waitingRoomId) => ({ url: `/waiting-rooms/${waitingRoomId}/join`, method: 'PATCH' }),
      invalidatesTags: cacher.invalidatesList(Tag.WAITING_ROOM),
    }),
    leaveWaitingRoom: builder.mutation<WaitingRoom, string>({
      query: (waitingRoomId) => ({ url: `/waiting-rooms/${waitingRoomId}/leave`, method: 'PATCH' }),
      invalidatesTags: cacher.invalidatesList(Tag.WAITING_ROOM),
    }),
  }),
  overrideExisting: false,
})
